import { type CSSProperties, type FC } from "react";
import cx from "classnames";
import type { AssistantMarkdownImage } from "../../types/assistant";
import { SIZES_FULL } from "../../utils/responsiveImage";
import { ResponsiveImage } from "../ResponsiveImage";
import { UnstyledButton } from "../UnstyledButton";
import { isPoiArtifact } from "../../scene/poiArtifacts/registry";

import styles from "./SceneImageCard.module.css";

type SceneImageCardProps = {
  image: AssistantMarkdownImage;
  buttonLabel: string;
  onEnterScene: (sceneId: string) => void;
  className?: string;
  style?: CSSProperties;
};

const CubeIcon = () => (
  <svg
    className={styles.badgeIcon}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 1.5L13.5 4.5V11.5L8 14.5L2.5 11.5V4.5L8 1.5Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path
      d="M8 14.5V7.5M8 7.5L2.5 4.5M8 7.5L13.5 4.5"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

export const SceneImageCard: FC<SceneImageCardProps> = ({
  image,
  buttonLabel,
  onEnterScene,
  className,
  style,
}) => {
  const sceneId = image.moveToSceneId ?? null;
  const has3D = sceneId !== null && isPoiArtifact(sceneId);

  const isPortrait = image.height > image.width;
  const aspectRatio = isPortrait ? 1 : image.width / image.height;

  const figureStyle: CSSProperties = {
    ...style,
    "--aspect-ratio": aspectRatio,
  } as CSSProperties;

  const content = (
    <>
      <div className={styles.imageWrap}>
        <ResponsiveImage
          className={styles.img}
          src={image.src}
          sizes={SIZES_FULL}
          originalWidth={image.width}
          width={image.width}
          height={image.height}
          alt={image.title ?? undefined}
        />
        {has3D && (
          <span className={styles.badge} aria-hidden="true">
            <CubeIcon />
            <span>{buttonLabel}</span>
          </span>
        )}
      </div>
      {image.title && <span className={styles.caption}>{image.title}</span>}
    </>
  );

  if (has3D && sceneId) {
    return (
      <UnstyledButton
        className={cx(styles.figure, styles.clickable, className)}
        style={figureStyle}
        onClick={() => onEnterScene(sceneId)}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label={buttonLabel}
      >
        {content}
      </UnstyledButton>
    );
  }

  return (
    <figure className={cx(styles.figure, className)} style={figureStyle}>
      {content}
    </figure>
  );
};
